<template>
  <v-container fluid class="zones-root py-5">
    <div class="zones-hero mb-4">
      <h1 class="zones-title mb-2">Управление зонами</h1>
      <p class="zones-subtitle mb-0">
        VK Maps редактор: добавляйте вершины кликом, перетаскивайте узлы, сохраняйте одну активную зону.
      </p>
    </div>

    <v-card class="zones-card" elevation="4">
      <v-card-title class="d-flex align-center">
        <span>Список зон</span>
        <v-spacer />
        <v-btn color="primary" @click="openZoneModal">Добавить зону</v-btn>
      </v-card-title>

      <v-list two-line>
        <v-list-item v-for="zone in zones" :key="zone.id">
          <v-list-item-content>
            <v-list-item-title>{{ zone.name }}</v-list-item-title>
            <v-list-item-subtitle>ID: {{ zone.id }}</v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action>
            <v-btn color="red" text @click="deleteZone(zone.id)">Удалить</v-btn>
          </v-list-item-action>
        </v-list-item>
      </v-list>
    </v-card>

    <v-dialog v-model="showModal" max-width="980px" persistent>
      <v-card class="zone-modal-card">
        <v-card-title class="headline">Добавить зону</v-card-title>

        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="zoneName"
                label="Название зоны"
                required
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="searchQuery"
                label="Поиск адреса"
                append-icon="mdi-magnify"
                :loading="searchingAddress"
                outlined
                dense
                @click:append="searchAddress"
                @keyup.enter="searchAddress"
              />
            </v-col>
          </v-row>

          <div class="controls-bar mb-3">
            <v-btn
              small
              color="primary"
              :outlined="!drawMode"
              @click="toggleDrawMode"
            >
              {{ drawMode ? 'Режим рисования включён' : 'Начать рисование' }}
            </v-btn>

            <v-btn small color="primary" text :disabled="zoneVertices.length < 3" @click="finishDrawing">
              Завершить контур
            </v-btn>

            <v-btn small text :disabled="!zoneVertices.length" @click="undoLastVertex">
              Undo вершину
            </v-btn>

            <v-btn small text color="red" :disabled="!zoneVertices.length" @click="clearZoneEditor">
              Очистить
            </v-btn>

            <v-chip x-small outlined class="ml-auto">
              Вершин: {{ zoneVertices.length }}
            </v-chip>
          </div>

          <div ref="zoneMap" class="zone-map">
            <div v-if="mapLoading" class="map-overlay">Загружаем VK Maps...</div>
            <div v-else-if="mapError" class="map-overlay map-overlay--error">{{ mapError }}</div>
          </div>

          <div class="mt-2 grey--text text--darken-1 caption">
            Подсказка: клик по карте добавляет вершину в режиме рисования. После завершения можно двигать узлы маркерами.
          </div>
        </v-card-text>

        <v-card-actions class="justify-end">
          <v-btn color="green" dark :loading="savingZone" @click="saveZone">Сохранить</v-btn>
          <v-btn text @click="closeZoneModal">Отмена</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { api } from '../lib/api'
import { loadVkMaps, VK_MAPS_API_KEY } from '../lib/vkMaps'

export default {
  name: 'AdminZones',
  data () {
    return {
      zones: [],
      showModal: false,
      zoneName: '',
      searchQuery: '',

      searchingAddress: false,
      savingZone: false,

      mapLoading: true,
      mapError: '',

      vk: null,
      map: null,

      drawMode: true,
      zoneVertices: [],
      vertexMarkers: [],

      polygonSourceId: 'zone-editor-polygon-source',
      lineSourceId: 'zone-editor-line-source'
    }
  },

  async mounted () {
    await this.loadZones()
  },

  beforeDestroy () {
    this.clearVertexMarkers()
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  },

  watch: {
    showModal (val) {
      if (val) {
        this.$nextTick(async () => {
          await this.initMap()
          setTimeout(() => {
            if (this.map && this.map.resize) this.map.resize()
          }, 200)
        })
      }
    }
  },

  methods: {
    showError (msg) {
      this.$toast ? this.$toast.error(msg) : alert(msg)
    },

    showSuccess (msg) {
      if (this.$toast && this.$toast.success) this.$toast.success(msg)
    },

    async loadZones () {
      try {
        const { data } = await api.get('/zones')
        this.zones = Array.isArray(data) ? data : []
      } catch (e) {
        console.error('loadZones failed:', e)
        this.showError('Не удалось загрузить зоны')
      }
    },

    openZoneModal () {
      this.resetEditorState()
      this.showModal = true
    },

    closeZoneModal () {
      this.showModal = false
      this.resetEditorState()
    },

    resetEditorState () {
      this.zoneName = ''
      this.searchQuery = ''
      this.drawMode = true
      this.zoneVertices = []
      this.clearVertexMarkers()
      this.renderZoneGeometry()
    },

    async initMap () {
      if (this.map) return

      this.mapLoading = true
      this.mapError = ''

      try {
        const vk = await loadVkMaps(VK_MAPS_API_KEY)
        this.vk = vk

        this.map = new vk.Map({
          container: this.$refs.zoneMap,
          center: [37.618423, 55.751244],
          zoom: 11,
          style: 'mmr://api/styles/main_style.json',
          hash: false
        })

        this.map.addControl(new vk.NavigationControl(), 'top-left')

        this.map.on('click', (e) => {
          this.handleMapClick(e)
        })

        this.map.on('load', () => {
          this.ensureEditorSources()
          this.renderZoneGeometry()
          this.mapLoading = false
        })
      } catch (e) {
        console.error('initMap failed:', e)
        this.mapLoading = false
        this.mapError = e && e.message ? e.message : 'Не удалось загрузить VK Maps'
      }
    },

    ensureEditorSources () {
      if (!this.map) return

      if (!this.map.getSource(this.polygonSourceId)) {
        this.map.addSource(this.polygonSourceId, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] }
        })
      }

      if (!this.map.getSource(this.lineSourceId)) {
        this.map.addSource(this.lineSourceId, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] }
        })
      }

      if (!this.map.getLayer('zone-editor-fill')) {
        this.map.addLayer({
          id: 'zone-editor-fill',
          type: 'fill',
          source: this.polygonSourceId,
          paint: {
            'fill-color': '#1f7af1',
            'fill-opacity': 0.18
          }
        })
      }

      if (!this.map.getLayer('zone-editor-outline')) {
        this.map.addLayer({
          id: 'zone-editor-outline',
          type: 'line',
          source: this.polygonSourceId,
          paint: {
            'line-color': '#1f7af1',
            'line-width': 2.2
          }
        })
      }

      if (!this.map.getLayer('zone-editor-line-preview')) {
        this.map.addLayer({
          id: 'zone-editor-line-preview',
          type: 'line',
          source: this.lineSourceId,
          paint: {
            'line-color': '#2158a7',
            'line-width': 2,
            'line-dasharray': [1.2, 1.2]
          }
        })
      }
    },

    toggleDrawMode () {
      this.drawMode = !this.drawMode
    },

    handleMapClick (e) {
      if (!this.drawMode) return
      if (!e || !e.lngLat) return

      this.zoneVertices.push([Number(e.lngLat.lng), Number(e.lngLat.lat)])
      this.renderZoneGeometry()
    },

    finishDrawing () {
      if (this.zoneVertices.length < 3) {
        this.showError('Нужно минимум 3 вершины')
        return
      }
      this.drawMode = false
      this.renderZoneGeometry()
    },

    undoLastVertex () {
      if (!this.zoneVertices.length) return
      this.zoneVertices.pop()
      this.renderZoneGeometry()
    },

    clearZoneEditor () {
      this.zoneVertices = []
      this.drawMode = true
      this.renderZoneGeometry()
    },

    clearVertexMarkers () {
      this.vertexMarkers.forEach(marker => marker.remove())
      this.vertexMarkers = []
    },

    renderZoneGeometry () {
      if (!this.map || this.mapLoading || this.mapError) return

      this.ensureEditorSources()
      this.clearVertexMarkers()

      const polygonSource = this.map.getSource(this.polygonSourceId)
      const lineSource = this.map.getSource(this.lineSourceId)
      if (!polygonSource || !lineSource) return

      const lineData = this.zoneVertices.length >= 2
        ? {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                geometry: {
                  type: 'LineString',
                  coordinates: this.zoneVertices
                },
                properties: {}
              }
            ]
          }
        : { type: 'FeatureCollection', features: [] }

      lineSource.setData(lineData)

      const polygonData = this.zoneVertices.length >= 3
        ? {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                geometry: {
                  type: 'Polygon',
                  coordinates: [this.getClosedRing()]
                },
                properties: {}
              }
            ]
          }
        : { type: 'FeatureCollection', features: [] }

      polygonSource.setData(polygonData)
      this.buildVertexMarkers()
    },

    buildVertexMarkers () {
      if (!this.vk || !this.map) return

      this.zoneVertices.forEach((coords, idx) => {
        const markerEl = document.createElement('button')
        markerEl.type = 'button'
        markerEl.className = 'zone-vertex-marker'
        markerEl.title = `Вершина ${idx + 1}`

        const marker = new this.vk.Marker({
          element: markerEl,
          draggable: true
        })
          .setLngLat(coords)
          .addTo(this.map)

        marker.on('dragend', () => {
          const lngLat = marker.getLngLat()
          this.$set(this.zoneVertices, idx, [Number(lngLat.lng), Number(lngLat.lat)])
          this.renderZoneGeometry()
        })

        this.vertexMarkers.push(marker)
      })
    },

    getClosedRing () {
      if (!this.zoneVertices.length) return []

      const ring = this.zoneVertices.map(c => [Number(c[0]), Number(c[1])])
      const first = ring[0]
      const last = ring[ring.length - 1]

      if (first[0] !== last[0] || first[1] !== last[1]) {
        ring.push([first[0], first[1]])
      }

      return ring
    },

    async searchAddress () {
      if (!this.searchQuery.trim()) return

      this.searchingAddress = true
      try {
        const { data } = await api.get('/search-address', {
          params: { q: this.searchQuery.trim(), format: 'json' }
        })

        if (!Array.isArray(data) || !data.length) {
          this.showError('Адрес не найден')
          return
        }

        const lat = Number(data[0].lat)
        const lon = Number(data[0].lon)
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
          this.showError('Геокодер вернул некорректные координаты')
          return
        }

        if (this.map) {
          this.map.flyTo({ center: [lon, lat], zoom: 15 })
        }
      } catch (e) {
        console.error('searchAddress failed:', e)
        this.showError('Ошибка поиска адреса')
      } finally {
        this.searchingAddress = false
      }
    },

    async saveZone () {
      if (!this.zoneName.trim()) {
        this.showError('Введите название зоны')
        return
      }

      if (this.zoneVertices.length < 3) {
        this.showError('Нарисуйте зону минимум из 3 вершин')
        return
      }

      const coordinates = [this.getClosedRing()]
      this.savingZone = true

      try {
        await api.post('/zones', {
          name: this.zoneName.trim(),
          coordinates
        })
        await this.loadZones()
        this.showSuccess('Зона сохранена')
        this.closeZoneModal()
      } catch (e) {
        console.error('saveZone failed:', e)
        this.showError('Не удалось сохранить зону')
      } finally {
        this.savingZone = false
      }
    },

    async deleteZone (id) {
      try {
        await api.delete(`/zones/${id}`)
        await this.loadZones()
        this.showSuccess('Зона удалена')
      } catch (e) {
        console.error('deleteZone failed:', e)
        this.showError('Не удалось удалить зону')
      }
    }
  }
}
</script>

<style scoped>
.zones-root {
  --hero-bg: radial-gradient(circle at 100% 0%, #d7f2ff 0%, #e8f7ff 33%, #f6fbff 100%);
  --card-bg: linear-gradient(145deg, #f8fbff 0%, #eff6ff 100%);
  background: linear-gradient(180deg, #f4f8ff 0%, #edf3ff 100%);
}

.zones-hero {
  padding: 18px 20px;
  border-radius: 16px;
  background: var(--hero-bg);
  border: 1px solid #d5e6fb;
}

.zones-title {
  font-size: 30px;
  line-height: 1.12;
  color: #1f2e4d;
}

.zones-subtitle {
  color: #55698f;
  font-size: 14px;
}

.zones-card,
.zone-modal-card {
  border-radius: 14px;
  border: 1px solid #d9e6ff;
  background: var(--card-bg);
}

.controls-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.zone-map {
  position: relative;
  height: 460px;
  width: 100%;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #dce8ff;
  background: #d8e7ff;
}

.map-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(244, 248, 255, 0.93);
  color: #2a3f65;
  font-weight: 600;
  z-index: 6;
}

.map-overlay--error {
  color: #8b1f1f;
}

@media (max-width: 960px) {
  .zones-title {
    font-size: 24px;
  }

  .zone-map {
    height: 400px;
  }
}

@media (max-width: 600px) {
  .zone-map {
    height: 330px;
  }
}
</style>

<style>
.zone-vertex-marker {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #ffffff;
  background: #0f73ee;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  cursor: grab;
  padding: 0;
}

.zone-vertex-marker:active {
  cursor: grabbing;
}
</style>
